import { RegularizationType } from "../../../types";
import { IRegularizationRequest } from "../../../store/apis/attendanceRegularization.api";
import {
  getWorkflowStatusFallbackLabel,
  isApprovedWorkflowStatus,
  isRejectedWorkflowStatus,
  WorkflowStatusCode,
} from "../../../utils/constants";
import { Modal2 } from "../../common";
import Badge from "../../common/Badge";
import { getWorkflowStatusVariant } from "../../../utils/badgeVariants";
import { getWorkflowStatusIcon } from "./tableColumns";

interface RequestDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: IRegularizationRequest;
  formatDate: (date: string) => string;
  formatTime: (time: string) => string;
  getRegularizationTypeLabel: (type: RegularizationType) => string;
  getStatusColor: (status: WorkflowStatusCode) => string;
}

const RequestDetailsModal: React.FC<RequestDetailsModalProps> = ({
  isOpen,
  onClose,
  request,
  formatDate,
  formatTime,
  getRegularizationTypeLabel,
  getStatusColor,
}) => (
  <Modal2 isOpen={isOpen} onClose={onClose} size="lg" title="Regularization Request Details">
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-[12.5px] font-semibold text-[var(--color-text-primary)] mb-[7px]">Employee</label>
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">
            {request.fullName} ({request.employeeId})
          </p>
        </div>
        <div>
          <label className="block text-[12.5px] font-semibold text-[var(--color-text-primary)] mb-[7px]">Department</label>
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">{request.departmentName}</p>
        </div>
        <div>
          <label className="block text-[12.5px] font-semibold text-[var(--color-text-primary)] mb-[7px]">Date</label>
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">{formatDate(request.date)}</p>
        </div>
        <div>
          <label className="block text-[12.5px] font-semibold text-[var(--color-text-primary)] mb-[7px]">Type</label>
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">
            {request.regularizationTypeName}
          </p>
        </div>
        <div>
          <label className="block text-[12.5px] font-semibold text-[var(--color-text-primary)] mb-[7px]">Requested Time</label>
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">
            {formatTime(request.requestedCheckInTime)} - {formatTime(request.requestedCheckOutTime)}
          </p>
        </div>
        <div>
          <label className="block text-[12.5px] font-semibold text-[var(--color-text-primary)] mb-[7px]">Actual Time</label>
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">
            {formatTime(request.actualCheckInTime || "")} - {formatTime(request.actualCheckOutTime || "")}
          </p>
        </div>
        <div>
          <label className="block text-[12.5px] font-semibold text-[var(--color-text-primary)] mb-[7px]">Status</label>
          <Badge
            variant={getWorkflowStatusVariant(request.status)}
            icon={<>{(() => { const Icon = getWorkflowStatusIcon(request.status); return <Icon className="w-4 h-4" />; })()}</>}
          >
            {(request as IRegularizationRequest & { statusLabel?: string, currentApproverName?: string }).statusLabel ||
              getWorkflowStatusFallbackLabel(request.status, (request as IRegularizationRequest & { currentApproverName?: string }).currentApproverName)}
          </Badge>
        </div>
      </div>
      <div>
        <label className="block text-[12.5px] font-semibold text-[var(--color-text-primary)] mb-[7px]">Reason</label>
        <p className="text-sm text-[var(--color-text-primary)]">{request.reason}</p>
      </div>
      {(() => {
        const extendedRequest = request as IRegularizationRequest & { approvedByName?: string };
        const approverName = extendedRequest.approvedByName;
        return approverName ? (
          <div>
            <label className="block text-[12.5px] font-semibold text-[var(--color-text-primary)] mb-[7px]">
              {isApprovedWorkflowStatus(request.status)
                ? "Approved By"
                : isRejectedWorkflowStatus(request.status)
                  ? "Rejected By"
                  : "Processed By"}
            </label>
            <p className="text-sm font-semibold text-[var(--color-text-primary)]">{approverName}</p>
          </div>
        ) : null;
      })()}
      {request.rejectionReason && (
        <div className="p-3 bg-[var(--color-error-light)] border border-red-200 rounded-[11px]">
          <label className="block text-[12.5px] font-bold text-[var(--color-error-dark)] uppercase tracking-[0.05em] mb-1">
            Rejection Reason
          </label>
          <p className="text-sm text-[var(--color-error-dark)]">{request.rejectionReason}</p>
        </div>
      )}
    </div>
  </Modal2>
);

export default RequestDetailsModal;

